/**
 * EmailForwardingPanel
 *
 * Portal self-serve email forwarding. Lets a customer point addresses on their
 * custom domain (e.g. info@theirdomain.com) at a real inbox. Rendered only when
 * the `email_forwarding` feature flag is on for the customer — the server
 * enforces the same flag, so this is UX gating, not the security boundary.
 *
 * The domain is derived server-side from the session; we never send it. Some
 * requests come back as decision:'escalate' (MX conflict or over quota) — those
 * are submitted to staff and we show a friendly "under review" message.
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Mail,
  Plus,
  Trash2,
  Loader2,
  CheckCircle,
  Clock,
  AlertCircle,
  Info,
} from 'lucide-react';
import {
  getForwardingStatus,
  setupForwarding,
  removeForwarding,
  type ForwardingStatus,
  type ForwardingAddress,
} from '../api/email-forwarding';

export function EmailForwardingPanel() {
  const [status, setStatus] = useState<ForwardingStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null); // amber "under review" / info

  // Add form
  const [localPart, setLocalPart] = useState('');
  const [destination, setDestination] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  const [removing, setRemoving] = useState<string | null>(null);

  const fetchStatus = useCallback(async () => {
    try {
      const data = await getForwardingStatus();
      setStatus(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load email forwarding');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const domain = status?.domain;
  const addresses = status?.addresses ?? [];
  const quota = status?.quota ?? 5;
  const usedCount = addresses.filter((a) => a.status !== 'disabled').length;
  const atQuota = usedCount >= quota;

  const handleAdd = async () => {
    const lp = localPart.trim().toLowerCase();
    const dest = destination.trim().toLowerCase();
    setError(null);
    setNotice(null);
    setSuccess(null);

    if (!lp) {
      setError('Enter the part before the @ (e.g. "info").');
      return;
    }
    if (!dest || !dest.includes('@')) {
      setError('Enter a valid destination email address.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await setupForwarding(lp, dest);
      if (result.decision === 'escalate') {
        setNotice(result.message);
      } else {
        setSuccess(result.message);
      }
      setLocalPart('');
      setDestination('');
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add forwarding address');
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemove = async (address: ForwardingAddress) => {
    const lp = address.address.split('@')[0];
    setRemoving(address.address);
    setError(null);
    setNotice(null);
    setSuccess(null);
    try {
      await removeForwarding(lp);
      setSuccess(`Removed forwarding for ${address.address}.`);
      await fetchStatus();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove forwarding address');
    } finally {
      setRemoving(null);
    }
  };

  const statusBadge = (s: ForwardingAddress['status']) => {
    switch (s) {
      case 'active':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-green-700 bg-green-50 px-2 py-0.5 rounded-full">
            <CheckCircle className="w-3 h-3" /> Active
          </span>
        );
      case 'pending-verification':
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
            <Clock className="w-3 h-3" /> Awaiting verification
          </span>
        );
      default:
        return (
          <span className="inline-flex items-center gap-1 text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
            Disabled
          </span>
        );
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm p-6">
        <div className="flex items-center gap-2 text-gray-400">
          <Loader2 className="w-4 h-4 animate-spin" />
          <span className="text-sm">Loading email forwarding…</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm p-6">
      <div className="flex items-start gap-4 mb-4">
        <div className="p-3 bg-primary-100 rounded-lg">
          <Mail className="w-6 h-6 text-primary-600" />
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900">Email Forwarding</h3>
          <p className="text-gray-600 text-sm mt-0.5">
            Forward addresses on{' '}
            <span className="font-medium text-gray-900">{domain || 'your domain'}</span> to any
            inbox — free.
          </p>
        </div>
      </div>

      {/* Messages */}
      {success && (
        <div className="mb-4 p-3 bg-green-50 text-green-700 rounded-lg flex items-start gap-2 text-sm">
          <CheckCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{success}</span>
        </div>
      )}
      {notice && (
        <div className="mb-4 p-3 bg-amber-50 text-amber-800 rounded-lg flex items-start gap-2 text-sm">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{notice}</span>
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg flex items-start gap-2 text-sm">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {/* Existing addresses */}
      {addresses.length > 0 && (
        <div className="mb-5 divide-y divide-gray-100 border border-gray-100 rounded-lg">
          {addresses.map((a) => (
            <div key={a.address} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-900 truncate">{a.address}</p>
                {a.destination && (
                  <p className="text-xs text-gray-500 truncate">→ {a.destination}</p>
                )}
              </div>
              {statusBadge(a.status)}
              <button
                onClick={() => handleRemove(a)}
                disabled={removing === a.address}
                title="Remove"
                className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors disabled:opacity-50"
              >
                {removing === a.address ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Trash2 className="w-4 h-4" />
                )}
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Add form */}
      {atQuota ? (
        <div className="p-3 bg-gray-50 text-gray-600 rounded-lg text-sm flex items-start gap-2">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            You're using all {quota} forwarding addresses. Need more? Add one and we'll review the
            request.
          </span>
        </div>
      ) : null}

      <div className="mt-2">
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-2">
          Add a forwarding address
        </label>
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="flex items-center flex-1 border border-gray-200 rounded-md focus-within:ring-2 focus-within:ring-primary-500 focus-within:border-primary-500 overflow-hidden">
            <input
              type="text"
              value={localPart}
              onChange={(e) => setLocalPart(e.target.value)}
              placeholder="info"
              className="flex-1 min-w-0 px-3 py-2 text-sm focus:outline-none"
            />
            <span className="px-2 py-2 text-sm text-gray-400 bg-gray-50 border-l border-gray-200 whitespace-nowrap">
              @{domain}
            </span>
          </div>
          <input
            type="email"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="you@gmail.com"
            className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          />
          <button
            onClick={handleAdd}
            disabled={submitting}
            className="flex items-center justify-center gap-2 px-4 py-2 bg-primary-500 hover:bg-primary-600 text-white text-sm font-medium rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Plus className="w-4 h-4" />
            )}
            Add
          </button>
        </div>
        <p className="text-xs text-gray-400 mt-2">
          {usedCount} of {quota} addresses used. New addresses may need a one-time verification
          click at the destination inbox.
        </p>
      </div>
    </div>
  );
}
